/**
 * **Il** componente di chat degli agenti. Uno solo, per tutti.
 *
 * Deciso da Enrico il 16-08-2026: *«facciamo un componente unico… prendiamo
 * proprio Aida come riferimento. È questa la UI di riferimento per il componente
 * che deve essere poi impiegato anche da tutti gli altri agenti»*.
 *
 * Prima erano due — `AidaSidebar` (M3) e `AgenteChatDrawer` (dashboard) — nati
 * in momenti diversi, ognuno con la propria veste. Non c'era una ragione
 * tecnica: l'unica differenza vera era *come stanno nella pagina*, e quella è
 * una proprietà, non un'identità. Finché restavano due sarebbero tornati a
 * divergere, e ogni agente nuovo avrebbe dovuto scegliere quale copiare — che è
 * esattamente come ci eravamo arrivati.
 *
 * **La forma è quella di Aida** (scelta di Enrico): barra verticale sul bordo,
 * pannello che occupa spazio invece di coprire, casella di testo che cresce con
 * Invio per mandare. Le funzioni migliori del vecchio drawer sono state
 * assorbite: storico, nuova conversazione, lettura **a frase** (parla mentre la
 * risposta arriva, non alla fine), preferenza voce ricordata, blocco durante
 * «Vedi come», ripresa dell'ultima conversazione all'apertura.
 *
 * Chi lo usa passa solo la propria identità e i propri comportamenti: vedi
 * `AidaSidebar` e `IrisDock`, che ora sono due configurazioni di poche righe.
 */
import { Link2, Loader2, Send, Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  AzioniAgente,
  HeaderAgente,
  LinguettaAgente,
  StoricoConversazioni,
} from "@/components/agenti/guscio";
import { MiniMarkdown } from "@/components/MiniMarkdown";
import { useVediComeStato } from "@/hooks/useVediCome";
import { streamChat } from "@/lib/agente-chat";
import { apiFetch } from "@/lib/api";
import { segmentiParlabili } from "@/lib/speakable";
import { TtsPlayer } from "@/lib/tts-player";
import { cn } from "@/lib/utils";

/** Larghezza del pannello aperto. Il guscio della pagina la riusa per il padding. */
export const CHAT_AGENTE_W = 420;

const VOCE_KEY = "agente-chat-voce";

interface Msg {
  ruolo: "utente" | "agente";
  testo: string;
  tool?: string | null;
  streaming?: boolean;
  /**
   * Gli altri agenti che hanno contribuito alla risposta («ho chiesto a Iris»).
   * Si mostra **in maniera discreta** sotto la bolla (scelta di Enrico, 17-08):
   * chi legge deve sapere da dove viene il dato, senza che diventi il protagonista.
   */
  fonti?: string[];
}

/** Apertura della chat da un «?» del tabellone: l'agente parla per primo. */
export interface AvvioChat {
  /** Cambia a ogni apertura: è ciò che fa ripartire la conversazione. */
  chiave: string;
  /** La domanda, ripetuta dall'agente come primo messaggio. */
  domanda: string;
  /** Id della richiesta: viaggia col turno così l'agente sa cosa chiudere. */
  richiestaId?: string;
}

interface ConvRow {
  id: string;
  titolo: string | null;
  creato_il: string;
  n_messaggi?: number;
  ultimo_messaggio_il?: string | null;
}

interface ConvDetail {
  conversazione_id: string;
  titolo: string | null;
  messaggi: { id: string; ruolo: "utente" | "assistente"; testo: string }[];
}

export interface ChatAgenteProps {
  aperta: boolean;
  onApertaChange: (v: boolean) => void;
  /** Nome dell'agente, come compare sulla barra e nell'intestazione. */
  nome: string;
  /** Riga sotto il nome: due parole su cosa fa. Le più lunghe si troncano. */
  stato: string;
  /** Base delle rotte chat: `/api/v1/{modulo}/agente/chat`. */
  basePath: string;
  /**
   * Come sta nella pagina. `inline` è il riferimento (la forma di Aida): occupa
   * spazio accanto al contenuto. `overlay` serve dove non c'è un bordo su cui
   * appoggiarsi, come dentro un widget della dashboard.
   */
  modo?: "inline" | "overlay";
  /** La barra sul bordo. Si nasconde dove l'apertura è comandata da altro. */
  mostraBarra?: boolean;
  /** Prima frase dell'agente a conversazione vuota. */
  benvenuto?: string;
  suggerimenti?: string[];
  /** Precompila la casella (apertura da un chip suggerito). */
  bozzaIniziale?: string;
  avvio?: AvvioChat;
  /** Un turno che ha usato strumenti può aver cambiato la pagina sotto. */
  onAzioneCompletata?: () => void;
}

export function ChatAgente({
  aperta,
  onApertaChange,
  nome,
  stato,
  basePath,
  modo = "inline",
  mostraBarra = true,
  benvenuto,
  suggerimenti = [],
  bozzaIniziale,
  avvio,
  onAzioneCompletata,
}: ChatAgenteProps) {
  const [messaggi, setMessaggi] = useState<Msg[]>([]);
  const [bozza, setBozza] = useState("");
  const [invio, setInvio] = useState(false);
  const [vista, setVista] = useState<"chat" | "storico">("chat");
  const [convAttiva, setConvAttiva] = useState<string | null>(null);
  const [storico, setStorico] = useState<ConvRow[]>([]);
  const [caricoStorico, setCaricoStorico] = useState(false);

  const conversazioneId = useRef<string | null>(null);
  const contestoDomanda = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inizializzato = useRef(false);

  const vediCome = useVediComeStato();
  const bloccato = !!vediCome;

  // ── voce ──────────────────────────────────────────────────────────────────
  const [voce, setVoce] = useState<boolean>(
    () => typeof window !== "undefined" && localStorage.getItem(VOCE_KEY) === "1",
  );
  const [parlando, setParlando] = useState(false);
  const voceRef = useRef(voce);
  const playerRef = useRef<TtsPlayer | null>(null);

  const player = () => {
    if (!playerRef.current) playerRef.current = new TtsPlayer(setParlando, basePath);
    return playerRef.current;
  };

  useEffect(() => {
    voceRef.current = voce;
  }, [voce]);
  useEffect(() => {
    if (!aperta) playerRef.current?.stop();
  }, [aperta]);
  useEffect(() => () => playerRef.current?.stop(), []);

  function toggleVoce() {
    const v = !voce;
    setVoce(v);
    localStorage.setItem(VOCE_KEY, v ? "1" : "0");
    if (!v) player().stop();
  }

  // ── storico ───────────────────────────────────────────────────────────────
  async function caricaStorico(): Promise<ConvRow[]> {
    setCaricoStorico(true);
    try {
      const r = await apiFetch<{ conversazioni: ConvRow[] }>(`${basePath}/conversazioni`);
      setStorico(r.conversazioni);
      return r.conversazioni;
    } catch {
      setStorico([]);
      return [];
    } finally {
      setCaricoStorico(false);
    }
  }

  async function apriConversazione(id: string) {
    player().stop();
    conversazioneId.current = id;
    setConvAttiva(id);
    setVista("chat");
    try {
      const r = await apiFetch<ConvDetail>(`${basePath}/conversazione/${id}`);
      setMessaggi(
        r.messaggi.map((m) => ({
          ruolo: m.ruolo === "utente" ? "utente" : "agente",
          testo: m.testo,
        })),
      );
    } catch {
      setMessaggi([]);
    }
  }

  function nuovaConversazione() {
    player().stop();
    conversazioneId.current = null;
    contestoDomanda.current = null;
    setConvAttiva(null);
    setMessaggi([]);
    setBozza("");
    setVista("chat");
  }

  // All'apertura riprende l'ultima conversazione, una volta sola. Chi arriva da
  // un «?» invece parte pulito: quella è una conversazione nuova per definizione.
  useEffect(() => {
    if (!aperta) {
      inizializzato.current = false;
      return;
    }
    if (inizializzato.current || avvio) return;
    inizializzato.current = true;
    void caricaStorico().then((righe) => {
      if (righe[0] && !invio) void apriConversazione(righe[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperta]);

  /**
   * Apertura da una domanda: conversazione NUOVA, con la domanda già detta
   * dall'agente. Nuova e non in coda, perché questa bolla è locale — non costa
   * una chiamata e non può essere riformulata storta — e mescolarla a uno
   * storico che il server non conosce farebbe divergere ciò che si vede da ciò
   * che il modello vede.
   */
  useEffect(() => {
    if (!avvio) return;
    conversazioneId.current = null;
    contestoDomanda.current = avvio.richiestaId ?? null;
    setConvAttiva(null);
    setMessaggi([{ ruolo: "agente", testo: avvio.domanda }]);
    setBozza("");
    setVista("chat");
  }, [avvio]);

  useEffect(() => {
    if (aperta && bozzaIniziale) setBozza(bozzaIniziale);
  }, [aperta, bozzaIniziale]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messaggi]);

  // La casella cresce col testo fino al tetto, poi scorre dentro: allargare il
  // pannello non impedirebbe a una frase lunga di uscire dalla vista.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [bozza]);

  // ── invio ─────────────────────────────────────────────────────────────────
  async function invia(testoArg?: string) {
    const testo = (testoArg ?? bozza).trim();
    if (!testo || invio || bloccato) return;
    setBozza("");
    setInvio(true);
    player().stop();
    setMessaggi((m) => [
      ...m,
      { ruolo: "utente", testo },
      { ruolo: "agente", testo: "", streaming: true, tool: null },
    ]);

    const upd = (fn: (m: Msg) => Msg) =>
      setMessaggi((arr) => {
        const next = [...arr];
        for (let i = next.length - 1; i >= 0; i--)
          if (next[i].ruolo === "agente") {
            next[i] = fn(next[i]);
            break;
          }
        return next;
      });

    // Lettura «a frase»: parla mentre la risposta arriva invece di aspettare la
    // fine — la differenza fra un assistente che risponde e uno che ti fa
    // aspettare in silenzio.
    let accumulato = "";
    let letteFinora = 0;
    const leggiNuove = (completo: boolean) => {
      if (!voceRef.current) return;
      const segs = segmentiParlabili(accumulato, completo);
      for (let k = letteFinora; k < segs.length; k++) player().parla(segs[k]);
      letteFinora = segs.length;
    };

    let haUsatoStrumenti = false;
    try {
      await streamChat(
        testo,
        conversazioneId.current,
        (ev) => {
          if (ev.tipo === "delta") {
            accumulato += ev.testo;
            upd((m) => ({ ...m, testo: m.testo + ev.testo, tool: null }));
            leggiNuove(false);
          } else if (ev.tipo === "tool") {
            haUsatoStrumenti = true;
            upd((m) => ({ ...m, tool: ev.nome }));
          } else if (ev.tipo === "fonte") {
            // La risposta ha attraversato un altro agente: lo si dice.
            upd((m) => ({
              ...m,
              fonti: m.fonti?.includes(ev.testo) ? m.fonti : [...(m.fonti ?? []), ev.testo],
            }));
          } else if (ev.tipo === "errore") {
            upd((m) => ({
              ...m,
              testo: m.testo || `⚠️ ${ev.messaggio}`,
              streaming: false,
              tool: null,
            }));
          } else if (ev.tipo === "fine") {
            upd((m) => ({ ...m, streaming: false, tool: null }));
            leggiNuove(true);
            if (ev.conversazione_id) {
              conversazioneId.current = ev.conversazione_id;
              setConvAttiva(ev.conversazione_id);
            }
            void caricaStorico();
          }
        },
        undefined,
        basePath,
        contestoDomanda.current,
      );
    } catch (e) {
      upd((m) => ({
        ...m,
        testo: m.testo || `⚠️ ${e instanceof Error ? e.message : "Errore di rete"}`,
        streaming: false,
        tool: null,
      }));
    } finally {
      setInvio(false);
      if (haUsatoStrumenti) onAzioneCompletata?.();
    }
  }

  // ── barra chiusa ──────────────────────────────────────────────────────────
  if (!aperta) {
    return mostraBarra ? (
      <LinguettaAgente
        nome={nome}
        onApri={() => onApertaChange(true)}
        modo={modo === "inline" ? "inline" : "flottante"}
      />
    ) : null;
  }

  // ── pannello aperto ───────────────────────────────────────────────────────
  return (
    <aside
      className={cn(
        "flex flex-col border-l border-border bg-card",
        modo === "inline"
          ? "h-full flex-none"
          : "fixed right-0 top-0 z-50 h-full shadow-xl",
      )}
      style={{ width: CHAT_AGENTE_W }}
      aria-label={`Chat con ${nome}`}
    >
      <HeaderAgente
        nome={nome}
        stato={stato}
        azioni={
          <AzioniAgente
            onNuova={nuovaConversazione}
            storicoAperto={vista === "storico"}
            onToggleStorico={() => {
              setVista((v) => {
                if (v === "chat") void caricaStorico();
                return v === "storico" ? "chat" : "storico";
              });
            }}
            voceAttiva={voce}
            parlando={parlando}
            onToggleVoce={toggleVoce}
            onFermaVoce={() => player().stop()}
            onChiudi={() => onApertaChange(false)}
          />
        }
      />

      {vista === "storico" ? (
        <div className="flex-1 overflow-y-auto">
          <StoricoConversazioni
            righe={storico}
            caricamento={caricoStorico}
            onApri={apriConversazione}
            attivaId={convAttiva}
          />
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messaggi.length === 0 && (
              <div className="space-y-3">
                {benvenuto && (
                  <div className="rounded-xl rounded-bl-sm border border-border bg-muted px-3 py-2.5 text-[13px] leading-relaxed">
                    {benvenuto}
                  </div>
                )}
                {suggerimenti.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[11px] font-semibold text-muted-foreground">
                      Prova a chiedere
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {suggerimenti.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => invia(s)}
                          disabled={bloccato}
                          className="rounded-full border border-brand-100 bg-brand-50 px-2.5 py-1 text-[11.5px] font-medium text-brand-700 transition-colors hover:bg-brand-600 hover:text-white disabled:opacity-50 dark:border-brand-900 dark:bg-brand-950"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {messaggi.map((m, i) => (
              <div
                key={i}
                className={cn(
                  "max-w-[92%] text-[13px] leading-relaxed",
                  m.ruolo === "utente" ? "ml-auto" : "",
                )}
              >
                <div
                  className={cn(
                    "rounded-xl px-3 py-2.5",
                    m.ruolo === "utente"
                      ? "rounded-br-sm bg-brand-700 text-white"
                      : "rounded-bl-sm border border-border bg-muted",
                  )}
                >
                  {m.ruolo === "agente" && m.tool && !m.testo ? (
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Wrench className="size-3.5 animate-pulse" /> {m.tool}…
                    </span>
                  ) : m.ruolo === "agente" ? (
                    <MiniMarkdown text={m.testo || "…"} />
                  ) : (
                    m.testo
                  )}
                </div>
                {/*
                  «Ho chiesto a Iris» — discreto, sotto la bolla e non dentro:
                  è provenienza, non contenuto. Chi legge deve sapere che la
                  risposta ha attraversato un altro agente (Enrico, 17-08).
                */}
                {m.ruolo === "agente" && m.fonti?.length ? (
                  <div className="mt-1 flex items-center gap-1.5 pl-1 text-[10.5px] text-muted-foreground">
                    <Link2 className="size-3 shrink-0" aria-hidden="true" />
                    {m.fonti.join(" · ")}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="border-t border-border p-3">
            {bloccato && (
              <p className="mb-2 rounded-md bg-muted px-2.5 py-1.5 text-[11.5px] text-muted-foreground">
                Stai guardando come un altro utente: la chat è in sola lettura.
              </p>
            )}
            <div className="flex items-end gap-2 rounded-2xl border border-border bg-muted py-1.5 pl-3.5 pr-1.5">
              <textarea
                ref={inputRef}
                rows={1}
                value={bozza}
                onChange={(e) => setBozza(e.target.value)}
                disabled={bloccato}
                onKeyDown={(e) => {
                  // Invio manda, Maiusc+Invio va a capo: la convenzione che chi
                  // scrive ha già nelle dita.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void invia();
                  }
                }}
                placeholder={
                  bloccato
                    ? "Disattiva «Vedi come» per scrivere"
                    : `Chiedi a ${nome}…  (Maiusc+Invio per andare a capo)`
                }
                aria-label="Messaggio"
                className="max-h-40 flex-1 resize-none self-center bg-transparent py-1 text-[13px] leading-relaxed outline-none placeholder:text-muted-foreground disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => void invia()}
                disabled={invio || bloccato || !bozza.trim()}
                aria-label="Invia"
                className="flex size-8 items-center justify-center rounded-full bg-brand-700 text-white transition-colors hover:bg-brand-800 disabled:opacity-40"
              >
                {invio ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
